import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createUnitBrand,
  createBusinessActivity,
  createCorporationPosition,
  createCustomerSource,
  createCustomerType,
  createUnitModel,
  createParameter,
  createParameterGroup,
  deleteParameter,
  deleteParameterGroup,
  deleteUnitBrand,
  deleteUnitModel,
  deleteBusinessActivity,
  deleteCorporationPosition,
  deleteCustomerSource,
  deleteCustomerType,
  getParameterColumns,
  getParameterGroupColumns,
  getParameterGroupLookup,
  getParameterGroupsList,
  getParameterLookup,
  getParametersByMakeId,
  getParametersList,
  getUnitBrandColumns,
  getUnitBrandLookup,
  getUnitBrandsList,
  getUnitModelColumns,
  getUnitModelLookupByMake,
  getUnitModelsList,
  getUnitTypeLookup,
  getBusinessActivitiesColumns,
  getBusinessActivitiesList,
  getCorporationPositionColumns,
  getCorporationPositionsList,
  getCustomerSourceColumns,
  getCustomerSourcesList,
  getCustomerTypeColumns,
  getCustomerTypesList,
  getSystemTypes,
  updateUnitBrand,
  updateUnitModel,
  updateUnitModelStatus,
  updateParameter,
  updateParameterGroup,
  updateBusinessActivity,
  updateCorporationPosition,
  updateCustomerSource,
  updateCustomerType,
  type GeneralSetupPayload,
  type ParameterGroupPayload,
  type ParameterPayload,
  type SetupListParams,
  type UnitBrandPayload,
  type UnitModelPayload,
} from "src/services/administrationSetupService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

export const useQueryGetCustomerTypesList = (params: SetupListParams = {}) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.customerTypeList, params, language],
      queryFn: () => getCustomerTypesList(params, language),
      refetchOnMount: true,
      retry: false,
    }),
    true
  );
};

export const useMutationCreateCustomerType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: (payload: GeneralSetupPayload) => createCustomerType(payload, language),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryNames.customerTypeList }),
  });
};
export const useMutationUpdateCustomerType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<GeneralSetupPayload> }) =>
      updateCustomerType(id, payload, language),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryNames.customerTypeList }),
  });
};
export const useMutationDeleteCustomerType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: (id: string | number) => deleteCustomerType(id, language),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryNames.customerTypeList }),
  });
};
export const useQueryGetCustomerTypeColumns = () => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useQuery({
    queryKey: [...QueryNames.customerTypeColumns, language],
    queryFn: () => getCustomerTypeColumns(language),
    retry: false,
  });
};

export const useQueryGetCustomerSourcesList = (params: SetupListParams = {}) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.customerSourceList, params, language],
      queryFn: () => getCustomerSourcesList(params, language),
      refetchOnMount: true,
      retry: false,
    }),
    true
  );
};
export const useMutationCreateCustomerSource = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: (payload: GeneralSetupPayload) => createCustomerSource(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.customerSourceList,
      }),
  });
};
export const useMutationUpdateCustomerSource = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<GeneralSetupPayload> }) =>
      updateCustomerSource(id, payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.customerSourceList,
      }),
  });
};
export const useMutationDeleteCustomerSource = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: (id: string | number) => deleteCustomerSource(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.customerSourceList,
      }),
  });
};
export const useQueryGetCustomerSourceColumns = () => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useQuery({
    queryKey: [...QueryNames.customerSourceColumns, language],
    queryFn: () => getCustomerSourceColumns(language),
    retry: false,
  });
};

export const useQueryGetCorporationPositionsList = (params: SetupListParams = {}) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.corporationPositionList, params, language],
      queryFn: () => getCorporationPositionsList(params, language),
      refetchOnMount: true,
      retry: false,
    }),
    true
  );
};
export const useMutationCreateCorporationPosition = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: (payload: GeneralSetupPayload) => createCorporationPosition(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.corporationPositionList,
      }),
  });
};
export const useMutationUpdateCorporationPosition = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<GeneralSetupPayload> }) =>
      updateCorporationPosition(id, payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.corporationPositionList,
      }),
  });
};
export const useMutationDeleteCorporationPosition = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: (id: string | number) => deleteCorporationPosition(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.corporationPositionList,
      }),
  });
};
export const useQueryGetCorporationPositionColumns = () => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useQuery({
    queryKey: [...QueryNames.corporationPositionColumns, language],
    queryFn: () => getCorporationPositionColumns(language),
    retry: false,
  });
};

export const useQueryGetBusinessActivitiesList = (params: SetupListParams = {}) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.businessActivitiesList, params, language],
      queryFn: () => getBusinessActivitiesList(params, language),
      refetchOnMount: true,
      retry: false,
    }),
    true
  );
};
export const useMutationCreateBusinessActivity = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: (payload: GeneralSetupPayload) => createBusinessActivity(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.businessActivitiesList,
      }),
  });
};
export const useMutationUpdateBusinessActivity = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<GeneralSetupPayload> }) =>
      updateBusinessActivity(id, payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.businessActivitiesList,
      }),
  });
};
export const useMutationDeleteBusinessActivity = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useMutation({
    mutationFn: (id: string | number) => deleteBusinessActivity(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.businessActivitiesList,
      }),
  });
};
export const useQueryGetBusinessActivitiesColumns = () => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useQuery({
    queryKey: [...QueryNames.businessActivitiesColumns, language],
    queryFn: () => getBusinessActivitiesColumns(language),
    retry: false,
  });
};
export const useQueryGetSystemTypes = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useQuery({
    queryKey: [...QueryNames.systemTypeList, language],
    queryFn: () => getSystemTypes(language),
    enabled,
    retry: false,
  });
};

export const useQueryGetUnitBrandsList = (
  params: SetupListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.unitBrandList, params, language],
      queryFn: () => getUnitBrandsList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useMutationCreateUnitBrand = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: UnitBrandPayload) => createUnitBrand(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.unitBrandList });
      queryClient.invalidateQueries({ queryKey: QueryNames.unitBrandLookup });
    },
  });
};

export const useMutationUpdateUnitBrand = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<UnitBrandPayload> }) =>
      updateUnitBrand(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.unitBrandList });
      queryClient.invalidateQueries({ queryKey: QueryNames.unitBrandLookup });
      queryClient.invalidateQueries({ queryKey: QueryNames.unitModelList });
    },
  });
};

export const useMutationDeleteUnitBrand = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteUnitBrand(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.unitBrandList });
      queryClient.invalidateQueries({ queryKey: QueryNames.unitBrandLookup });
      queryClient.invalidateQueries({ queryKey: QueryNames.unitModelList });
    },
  });
};

export const useQueryGetUnitBrandLookup = (
  enabled = true,
  options: Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.unitBrandLookup, language],
    queryFn: () => getUnitBrandLookup({}, language),
    enabled,
    retry: false,
    ...options,
  });
};

export const useQueryGetUnitBrandColumns = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.unitBrandColumns, language],
    queryFn: () => getUnitBrandColumns(language),
    enabled,
    retry: false,
  });
};

export const useQueryGetUnitModelsList = (
  params: SetupListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.unitModelList, params, language],
      queryFn: () => getUnitModelsList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useMutationCreateUnitModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: UnitModelPayload) => createUnitModel(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.unitModelList });
    },
  });
};

export const useMutationUpdateUnitModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<UnitModelPayload> }) =>
      updateUnitModel(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.unitModelList });
    },
  });
};

export const useMutationUpdateUnitModelStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string | number; active: boolean }) =>
      updateUnitModelStatus(id, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.unitModelList });
    },
  });
};

export const useMutationDeleteUnitModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteUnitModel(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.unitModelList });
    },
  });
};

export const useQueryGetUnitModelColumns = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.unitModelColumns, language],
    queryFn: () => getUnitModelColumns(language),
    enabled,
    retry: false,
  });
};

export const useQueryGetUnitTypeLookup = (
  enabled = true,
  options: Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.unitTypeLookup, language],
    queryFn: () => getUnitTypeLookup(language),
    enabled,
    retry: false,
    ...options,
  });
};

export const useQueryGetUnitBrandLookupByType = (
  unitTypeId: string | number | undefined,
  enabled = true
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.unitBrandLookup, "byType", unitTypeId, language],
    queryFn: () => getUnitBrandLookup({ unitTypeId }, language),
    enabled: enabled && unitTypeId !== undefined,
    retry: false,
  });
};

export const useQueryGetUnitModelLookupByMake = (
  makeId: string | number | undefined,
  unitTypeId: string | number | undefined,
  enabled = true
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.unitModelList, "byMake", makeId, unitTypeId, language],
    queryFn: () =>
      getUnitModelLookupByMake(makeId as string | number, { unitType: unitTypeId }, language),
    enabled: enabled && makeId !== undefined,
    retry: false,
  });
};

export const useQueryGetParameterGroupsList = (
  params: SetupListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.parameterGroupList, params, language],
      queryFn: () => getParameterGroupsList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useMutationCreateParameterGroup = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: ParameterGroupPayload) => createParameterGroup(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterGroupList });
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterGroupLookup });
    },
  });
};

export const useMutationUpdateParameterGroup = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Partial<ParameterGroupPayload>;
    }) => updateParameterGroup(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterGroupList });
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterGroupLookup });
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterList });
    },
  });
};

export const useMutationDeleteParameterGroup = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteParameterGroup(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterGroupList });
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterGroupLookup });
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterList });
    },
  });
};

export const useQueryGetParameterGroupLookup = (
  enabled = true,
  options: Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.parameterGroupLookup, language],
    queryFn: () => getParameterGroupLookup(language),
    enabled,
    retry: false,
    ...options,
  });
};

export const useQueryGetParameterGroupColumns = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.parameterGroupColumns, language],
    queryFn: () => getParameterGroupColumns(language),
    enabled,
    retry: false,
  });
};

export const useQueryGetParametersList = (
  params: SetupListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.parameterList, params, language],
      queryFn: () => getParametersList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useMutationCreateParameter = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: ParameterPayload) => createParameter(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterList });
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterLookup });
    },
  });
};

export const useMutationUpdateParameter = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<ParameterPayload> }) =>
      updateParameter(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterList });
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterLookup });
    },
  });
};

export const useMutationUpdateParameterStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, active }: { id: string | number; active: boolean }) =>
      updateParameter(id, { id, active } as Partial<ParameterPayload>, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterList });
    },
  });
};

export const useMutationDeleteParameter = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteParameter(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterList });
      queryClient.invalidateQueries({ queryKey: QueryNames.parameterLookup });
    },
  });
};

export const useQueryGetParameterColumns = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.parameterColumns, language],
    queryFn: () => getParameterColumns(language),
    enabled,
    retry: false,
  });
};

export const useQueryGetParameterLookup = (
  enabled = true,
  options: Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.parameterLookup, language],
    queryFn: () => getParameterLookup(language),
    enabled,
    retry: false,
    ...options,
  });
};

export const useQueryGetParametersByMakeId = (id: string | number | undefined, enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.parameterLookup, id, language],
    queryFn: () => getParametersByMakeId(id!, language),
    enabled: enabled && id !== undefined && id !== null && id !== "",
    retry: false,
  });
};
